/**
 * One scene in the story preview pane and the pop-out (spec 009): the
 * canonical player (`PlayerHost`) with a transport (`PreviewTransport`):
 * ▶ Play / ⏸ Pause / ⏹ Stop, the time and a scrub bar.
 *
 * - ⏸ then ▶ resumes from the paused time; ▶ after Stop or the end plays from 0.
 * - Scrubbing seeks without playing (a running play continues from there).
 * - Stop returns to 0.
 * - At the choice reveal the player pauses with the panel clickable (as the
 *   scene editor's ▶ Preview); clicking an option shows "Chose: …" and ends
 *   the playback.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ChoiceOption, Orientation } from '../../../../Shared/src/@types';
import type { PlayableSceneInput } from '../../../../Shared/src/runtime/playableScene';
import { PlayerHost, type PlayerHostHandle } from './PlayerHost';
import { PreviewTransport } from './PreviewTransport';
import styles from '../StoryPreviewPane.module.scss';

type Status = 'idle' | 'playing' | 'paused' | 'held' | 'ended';

interface SingleScenePreviewProps {
  scene: PlayableSceneInput | null;
  loading?: boolean;
  orientation: Orientation;
  /** Where the transport goes (the pane hides it while popped out). */
  showTransport?: boolean;
  wrapClassName?: string;
}

export const SingleScenePreview: React.FC<SingleScenePreviewProps> = ({
  scene,
  loading = false,
  orientation,
  showTransport = true,
  wrapClassName,
}) => {
  const hostRef = useRef<PlayerHostHandle>(null);
  /** Whether a scrub interrupted a running play (it resumes on release). */
  const scrubResume = useRef(false);
  const [status, setStatus] = useState<Status>('idle');
  const [playhead, setPlayhead] = useState(0);
  const [length, setLength] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  // A different scene starts over
  const sceneId = scene?.id;
  useEffect(() => {
    setStatus('idle');
    setPlayhead(0);
  }, [sceneId]);

  // The play length is the runtime's
  useEffect(() => {
    setLength(hostRef.current?.getLength() ?? 0);
  }, [scene, orientation]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const handlePlay = useCallback(() => {
    const host = hostRef.current;
    if (!host) return;
    setToast(null);
    if (status === 'paused') host.resume();
    else host.play(0);
    setLength(host.getLength());
    setStatus('playing');
  }, [status]);

  const handlePause = useCallback(() => {
    hostRef.current?.pause();
    setStatus('paused');
  }, []);

  const handleStop = useCallback(() => {
    const host = hostRef.current;
    if (!host) return;
    host.stop();
    host.seek(0);
    setStatus('idle');
  }, []);

  const handleChoice = useCallback((choice: ChoiceOption) => {
    const choices = scene?.choices ?? [];
    const label = choice.text.trim() || `Option ${choices.findIndex((c) => c.id === choice.id) + 1}`;
    setToast(`Chose: ${label}`);
    const host = hostRef.current;
    host?.stop();
    host?.seek(0);
    setStatus('idle');
  }, [scene]);

  // Scrub: seek without playing; a running play continues from the new time
  const handleScrubStart = useCallback(() => {
    const host = hostRef.current;
    scrubResume.current = status === 'playing' && !!host?.isPlaying();
    if (scrubResume.current) host!.pause();
  }, [status]);

  const handleScrub = useCallback((ms: number) => {
    hostRef.current?.seek(ms);
  }, []);

  const handleScrubEnd = useCallback(() => {
    if (scrubResume.current) hostRef.current?.resume();
    else if (status === 'idle' || status === 'ended') setStatus('paused');
    scrubResume.current = false;
  }, [status]);

  const playingNow = status === 'playing';

  return (
    <>
      <div className={wrapClassName ?? styles.canvasWrap} data-testid="preview-stage">
        {loading && (
          <div className={styles.loadingOverlay}><div className={styles.spinner} /></div>
        )}
        {scene && (
          <PlayerHost
            ref={hostRef}
            scene={scene}
            orientation={orientation}
            onPlayhead={setPlayhead}
            onReveal={() => setStatus('held')}
            onChoice={handleChoice}
            onComplete={(e) => { if (e.exitType === 'auto') setStatus('ended'); }}
          />
        )}
        {toast && (
          <div className={styles.previewToast} role="status" data-testid="preview-toast">{toast}</div>
        )}
      </div>

      {showTransport && scene && (
        <PreviewTransport
          playing={playingNow}
          playheadMs={playhead}
          lengthMs={length}
          onPlay={handlePlay}
          onPause={handlePause}
          onStop={handleStop}
          stopDisabled={status === 'idle' && playhead === 0}
          onScrubStart={handleScrubStart}
          onScrub={handleScrub}
          onScrubEnd={handleScrubEnd}
        />
      )}
    </>
  );
};

export default SingleScenePreview;
